import { Avatar, Flex, Text } from '@nula/core-ui';

import { resolveAttendeePhotoUrl } from '@/lib/recordings/resolve-attendee-photo';
import type { AttendeeView } from '@/lib/recordings/types/attendees';
import styles from './attendee-card-content.module.css';

// ── Shared avatar helper ───────────────────────────────────────────────────────

export function AvatarFor({
  attendee,
  name,
  size,
}: {
  attendee: AttendeeView;
  name: string;
  size: number;
}) {
  // Shared chain: Apollo photoUrl → Gravatar (enriched) → initials. Same
  // resolver the transcript speaker avatar uses so both surfaces match.
  const src = resolveAttendeePhotoUrl(attendee);
  const initials = initialsOf(name, attendee.email);

  return (
    <Avatar src={src} alt={name} size={size}>
      {initials}
    </Avatar>
  );
}

function initialsOf(name: string, email: string): string {
  const source = name && name !== email ? name : email.split('@')[0];
  return source
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

// ── Card content ───────────────────────────────────────────────────────────────

export type AttendeeCardContentProps = {
  attendee: AttendeeView;
  name: string;
};

/**
 * Rich card body for an enriched attendee. Renders photo + name + LinkedIn
 * link + title/company subtitle + company description + location/email footer.
 *
 * Does NOT render an outer container — callers are responsible for wrapping.
 * This design lets the component be used inside `AttendeeRow`'s animated
 * expand panel AND inside `SpeakerHoverCard`'s popover without coupling either
 * container to the other.
 */
export function AttendeeCardContent({ attendee, name }: AttendeeCardContentProps) {
  // Guard against non-https URIs (e.g. javascript:) — defense-in-depth against
  // future attacker-controlled values reaching the DB via enrichment providers.
  const safeLinkedinUrl =
    attendee.linkedinUrl?.startsWith('https://') ? attendee.linkedinUrl : null;

  return (
    <Flex vertical gap="sm" className={styles.inner}>
      <Flex align="start" gap="md">
        <AvatarFor attendee={attendee} name={name} size={40} />
        <Flex vertical className={styles.header}>
          <Flex align="baseline" justify="space-between" gap="sm">
            <Text className={styles.name}>{name}</Text>
            {safeLinkedinUrl ? (
              <a
                href={safeLinkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.linkedinLink}
              >
                LinkedIn ↗
              </a>
            ) : null}
          </Flex>
          {attendee.title && attendee.companyName ? (
            <Text size="xs" className={styles.title}>
              {attendee.title} at{' '}
              <Text className={styles.companyName}>{attendee.companyName}</Text>
            </Text>
          ) : null}
        </Flex>
      </Flex>
      {attendee.companyDescription ? (
        <Text size="xs" className={styles.description}>
          {attendee.companyDescription}
        </Text>
      ) : null}
      <Flex gap="sm" className={styles.footer}>
        {attendee.location ? (
          <Text size="xs" className={styles.footerItem}>{attendee.location}</Text>
        ) : null}
        <Text size="xs" className={styles.footerItem}>{attendee.email}</Text>
      </Flex>
    </Flex>
  );
}
